import posthog from 'posthog-js';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;
const UNMASKED_ROLES = new Set(['teacher', 'admin', 'superadmin']);

export function initAnalytics() {
  if (initialized || !KEY) return;
  posthog.init(KEY, {
    api_host: HOST,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    // Do not start session recording until we know who the user is.
    // We opt in for teachers/admins on identifyUser(); students are never recorded.
    // This also means anonymous visitors aren't recorded by default.
    disable_session_recording: true,
    session_recording: {
      // If recording ever starts, still block the writing surface.
      blockSelector: '.document-canvas, .editor-canvas, [data-student-content]',
    },
    persistence: 'localStorage',
    loaded: () => {
      initialized = true;
    },
  });
  initialized = true;
}

export function identifyUser(
  userId: string | number,
  role?: string,
  email?: string,
  name?: string,
) {
  if (!KEY) return;
  // Build person properties. Omit empty fields so we don't overwrite real values
  // with blanks on later identify calls.
  const props: Record<string, unknown> = {};
  if (role) props.role = role;
  if (email) props.email = email;
  if (name) props.name = name;
  posthog.identify(String(userId), Object.keys(props).length ? props : undefined);
  // Start session recording ONLY for teachers/admins. Students and anonymous
  // users are never session-recorded, for FERPA safety.
  if (role && UNMASKED_ROLES.has(role)) {
    try { posthog.startSessionRecording(); } catch (e) { /* older posthog-js */ }
  } else {
    try { posthog.stopSessionRecording(); } catch (e) { /* older posthog-js */ }
  }
}

export function resetUser() {
  if (!KEY) return;
  try { posthog.stopSessionRecording(); } catch (e) { /* older posthog-js */ }
  posthog.reset();
}

export function track(event: string, props?: Record<string, unknown>) {
  if (!KEY) return;
  posthog.capture(event, props);
}

export const Events = {
  Signup: 'signup',
  Login: 'login',
  Logout: 'logout',
  CreateDocument: 'create_document',
  SaveDocument: 'save_document',
  PickTemplate: 'pick_template',
  SubmitStory: 'submit_story',
  UnsubmitStory: 'unsubmit_story',
  CreateClassroom: 'create_classroom',
  CreateNewspaper: 'create_newspaper',
  CreateEdition: 'create_edition',
  PublishEdition: 'publish_edition',
  ImportFromGoogleClassroom: 'import_from_google_classroom',
  StudentJoinedClassroom: 'student_joined_classroom',
  StudentStartedWriting: 'student_started_writing',
  ClickedCTA: 'clicked_cta',
  HomepageDemoUsed: 'homepage_demo_used',
  HomepageWindowDragged: 'homepage_window_dragged',
  HomepageWindowClosed: 'homepage_window_closed',
} as const;
